import { useMemo } from 'react';
import { SHIPPING_EXCLUDED_STATES } from '~/constants/srp';
import { useUserDeliveryInfo } from '~/context/srp/InventoryContext';
import { useTileDataContext } from '~/context/srp/TileDataContext';
import { useUserLocation } from '~/context/srp/UserContext';
import { TWO_HOURS } from './constants';
import { getDeliveryOrPickupByVehicle } from './helpers/ResultTile.helpers';
import { InventoryTypeId } from './types';

export const useFreeShippingEligible = () => {
  const { isOutOfMarket = false, isDeliveryAllowed = false } =
    useUserDeliveryInfo() ?? {};

  return !isOutOfMarket && isDeliveryAllowed;
};

export const useShippingCost = () => {
  const { tileData: { transportCost = 0 } = {} } = useTileDataContext();

  return transportCost;
};

export const useHasNoShipping = () => {
  const { state = 'AZ' } = useUserLocation();

  return SHIPPING_EXCLUDED_STATES.includes(state.toUpperCase());
};

export const useHideShipping = () => {
  const { tileData } = useTileDataContext();
  const hasShippingRestriction = useHasNoShipping();
  const isLocalDeliveryOnly =
    tileData.deliveryInfo == null ||
    tileData.deliveryInfo.activityType === 'LocalDelivery';

  return hasShippingRestriction || !isLocalDeliveryOnly;
};

export const usePurchaseStatus = () => {
  const { tileData } = useTileDataContext();

  return useMemo(() => {
    if (!tileData?.vehicleUnlockDateTime) {
      return { isLocked: false, isOnHold: false };
    }

    const timeLeft = Date.parse(tileData?.vehicleUnlockDateTime) - +new Date();

    return {
      isLocked: Math.floor(timeLeft / 1000) >= TWO_HOURS,
      isOnHold: Math.floor(timeLeft / 1000) < TWO_HOURS
    };
  }, [tileData.vehicleUnlockDateTime]);
};

export const useHideGetItBy = (getItBy: string) => {
  const { tileData } = useTileDataContext();
  const { isLocked, isOnHold } = usePurchaseStatus();

  return Boolean(isLocked || isOnHold || tileData?.isOnDemand || !getItBy);
};

export function useDeliveryOrPickupChipGetItBy(): ReturnType<
  typeof getDeliveryOrPickupByVehicle
> {
  const { tileData } = useTileDataContext();

  return getDeliveryOrPickupByVehicle(tileData);
}

export function useShowSkeleton() {
  const { tileData } = useTileDataContext();

  return !tileData?.vehicleId;
}

type VehicleDiscount = {
  amount: number;
  type: string;
};

export const useDiscount = () => {
  const { tileData } = useTileDataContext();

  const discountedPrice = tileData?.price?.total ?? 99999;
  const totalDiscount = tileData?.price?.totalDiscountAmount ?? 0;

  return useMemo(() => {
    const discounts = tileData?.price?.discounts ?? [];
    const tradeIncentiveDiscount = discounts.find(
      (discount: VehicleDiscount) => discount?.type === 'TradeIncentive'
    );
    const hasOtherDiscount = discounts.find(
      (discount: VehicleDiscount) => discount?.type !== 'TradeIncentive'
    );
    const tradeIncentive = tradeIncentiveDiscount?.amount ?? 0;

    return {
      discounts,
      discountedPrice,
      hasDiscount: totalDiscount > 0,
      hasOtherDiscount,
      totalDiscount,
      tradeIncentive
    };
  }, [discountedPrice, totalDiscount, tileData?.price?.discounts]);
};

const useInventoryType = () => {
  const { tileData } = useTileDataContext();

  return useMemo(
    () => ({
      vehicleInventoryType: tileData?.vehicleInventoryType,
      storeKey: tileData?.storeKey?.toLowerCase()
    }),
    [tileData?.vehicleInventoryType, tileData?.storeKey]
  );
};

export const useIsMarketPlace = (): boolean => {
  const { vehicleInventoryType, storeKey } = useInventoryType();

  return vehicleInventoryType === 4 || storeKey === 'dmp2core';
};

export const usePriceTerms = () => {
  const { tileData } = useTileDataContext();

  return useMemo(() => {
    const {
      monthlyPayment = undefined,
      downPayment = undefined,
      downPaymentDetails = undefined,
      apr = undefined,
      term = undefined,
      pricingErrorMessage
    } = tileData?.price || {};
    const hasTerms =
      monthlyPayment != null &&
      downPayment != null &&
      apr != null &&
      term != null;
    const totalCashDue = downPaymentDetails?.totalCashDue || undefined;

    return {
      monthlyPayment,
      downPayment,
      apr,
      term,
      totalCashDue,
      pricingErrorMessage,
      hasTerms
    };
  }, [tileData?.price]);
};

export const useHasFinanceFailed = () => {
  const terms = usePriceTerms();

  const hasErrored = !terms.pricingErrorMessage && !terms.hasTerms;

  return hasErrored;
};

export const useIsPreOrder = () => {
  const { tileData } = useTileDataContext();
  const { isLocked, isOnHold } = usePurchaseStatus();

  if (isLocked || isOnHold) {
    return false;
  }

  return tileData?.vehicleInventoryType === InventoryTypeId.WIP;
};

export const useIsReservable = () => {
  const { tileData } = useTileDataContext();
  const { isLocked, isOnHold } = usePurchaseStatus();

  if (isLocked || isOnHold) {
    return false;
  }

  return tileData?.vehiclePurchaseType === 'Reservable';
};

export const useIsPreOrderable = () => {
  const isPreOrder = useIsPreOrder();
  const isReservable = useIsReservable();

  return isReservable || isPreOrder;
};
